import React, { useState, useEffect } from 'react';
import { Users, Shield, Search, Check, X, AlertCircle, UserPlus } from 'lucide-react';

const UserAdminView = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [updating, setUpdating] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', role: 'sales', displayName: '' });
  const [addingUser, setAddingUser] = useState(false);

  useEffect(() => {
    fetchCurrentUser();
    fetchUsers();
  }, []);

  // Helper to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const fetchCurrentUser = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${API_URL}/auth/user`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      const data = await response.json();
      setCurrentUser(data);
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${API_URL}/api/admin/users`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Access denied');
      }

      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('Access denied: Admin privileges required');
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (userId, newRole) => {
    if (!window.confirm(`Change user role to ${newRole}?`)) return;

    setUpdating({ ...updating, [userId]: true });
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ role: newRole })
      });

      if (!response.ok) throw new Error('Failed to update role');

      await fetchUsers();
    } catch (error) {
      console.error('Error updating role:', error);
      alert('Failed to update user role');
    } finally {
      setUpdating({ ...updating, [userId]: false });
    }
  };

  const toggleUserStatus = async (userId, isActive) => {
    if (!window.confirm(`${isActive ? 'Deactivate' : 'Activate'} this user?`)) return;

    setUpdating({ ...updating, [userId]: true });
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ is_active: !isActive })
      });

      if (!response.ok) throw new Error('Failed to update status');

      await fetchUsers();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update user status');
    } finally {
      setUpdating({ ...updating, [userId]: false });
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    if (!newUser.email || !newUser.email.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }

    setAddingUser(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${API_URL}/api/admin/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify(newUser)
      });

      if (response.status === 409) {
        alert('A user with this email already exists');
        return;
      }

      if (!response.ok) throw new Error('Failed to create user');

      await fetchUsers();
      setNewUser({ email: '', role: 'sales', displayName: '' });
      setShowAddForm(false);
      alert('User created successfully! They can now log in with their assigned role.');
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Failed to create user');
    } finally {
      setAddingUser(false);
    }
  };

  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.display_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const roleColors = {
    super_admin: 'bg-red-100 text-red-800 border-red-400',
    admin: 'bg-red-100 text-red-700 border-red-300',
    ceo: 'bg-amber-100 text-amber-800 border-amber-400',
    cro: 'bg-amber-100 text-amber-700 border-amber-300',
    cfo: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    cto: 'bg-amber-100 text-amber-600 border-amber-300',
    manager: 'bg-purple-100 text-purple-700 border-purple-300',
    sales: 'bg-blue-100 text-blue-700 border-blue-300',
    presales: 'bg-cyan-100 text-cyan-700 border-cyan-300',
    implementation: 'bg-teal-100 text-teal-700 border-teal-300',
    other: 'bg-slate-100 text-slate-600 border-slate-300'
  };

  const roleLabels = {
    super_admin: 'Super Admin',
    admin: 'Admin',
    ceo: 'CEO',
    cro: 'CRO',
    cfo: 'CFO',
    cto: 'CTO',
    manager: 'Manager',
    sales: 'Account Executive',
    presales: 'Presales',
    implementation: 'Implementation',
    other: 'Other'
  };

  const rolePermissions = {
    super_admin: ['Full system access', 'Edit all quotes', 'All settings'],
    admin: ['Manage users', 'View all quotes', 'Edit own quotes', 'Settings'],
    ceo: ['View all quotes', 'Approve all quotes', 'Analytics'],
    cro: ['View all quotes', 'Approve all quotes', 'Analytics'],
    cfo: ['View all quotes', 'Analytics (read-only)'],
    cto: ['View all quotes', 'Approve all quotes', 'Analytics'],
    manager: ['View all quotes', 'Approve quotes', 'Analytics'],
    sales: ['Own quotes only', 'Create quotes'],
    presales: ['View all quotes', 'Analytics (read-only)'],
    implementation: ['View all quotes', 'Analytics (read-only)'],
    other: ['Analytics (read-only)']
  };

  // Normalize role to lowercase for comparisons
  const userRole = currentUser?.role?.toLowerCase();

  // Check if current user can manage users
  const canManageUsers = currentUser?.permissions?.canManageUsers ||
    ['super_admin', 'admin'].includes(userRole);

  // Check if current user can remove/deactivate users
  const canRemoveUsers = currentUser?.permissions?.canRemoveUsers ||
    ['super_admin', 'admin'].includes(userRole);

  if (!canManageUsers && currentUser) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
          <AlertCircle size={48} className="mx-auto text-red-600 mb-4" />
          <h2 className="text-xl font-bold text-red-900 mb-2">Access Denied</h2>
          <p className="text-red-700">You need admin privileges to access user management.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-800 mb-2 flex items-center gap-3">
          <Shield size={32} className="text-blue-600" />
          User Administration
        </h1>
        <p className="text-slate-600">{users.length} total users</p>
      </div>

      {/* Search and Add User */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex gap-4 items-center">
          <div className="relative flex-1">
            <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search by email or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium flex items-center gap-2 hover:bg-blue-700 transition-colors"
          >
            <UserPlus size={18} />
            Add User
          </button>
        </div>

        {/* Add User Form */}
        {showAddForm && (
          <form onSubmit={createUser} className="mt-4 pt-4 border-t border-slate-200">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                <input
                  type="email"
                  required
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="user@splashbi.com"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Display Name</label>
                <input
                  type="text"
                  value={newUser.displayName}
                  onChange={(e) => setNewUser({ ...newUser, displayName: e.target.value })}
                  placeholder="John Smith"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Role *</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="sales">Account Executive</option>
                  <option value="presales">Presales</option>
                  <option value="implementation">Implementation</option>
                  <option value="other">Other</option>
                  <option value="manager">Manager</option>
                  <option value="cfo">CFO (View Only)</option>
                  <option value="cro">CRO</option>
                  <option value="cto">CTO</option>
                  <option value="ceo">CEO</option>
                  <option value="admin">Admin</option>
                  {userRole === 'super_admin' && (
                    <option value="super_admin">Super Admin</option>
                  )}
                </select>
              </div>
              <div className="flex items-end gap-2">
                <button
                  type="submit"
                  disabled={addingUser}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {addingUser ? 'Creating...' : 'Create User'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
            <p className="mt-2 text-sm text-slate-500">
              Pre-create a user so they'll have their role assigned on first login via SSO.
            </p>
          </form>
        )}
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500">Loading users...</div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">User</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Role</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Permissions</th>
                <th className="text-center px-6 py-4 text-sm font-semibold text-slate-700">Last Login</th>
                <th className="text-center px-6 py-4 text-sm font-semibold text-slate-700">Status</th>
                <th className="text-center px-6 py-4 text-sm font-semibold text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                        {user.display_name?.charAt(0) || user.email.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium text-slate-800">{user.display_name || 'No name'}</div>
                        <div className="text-sm text-slate-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <select
                      value={user.role}
                      onChange={(e) => updateUserRole(user.id, e.target.value)}
                      disabled={updating[user.id] || user.id === currentUser.id}
                      className={`px-3 py-1 rounded-lg text-sm font-semibold border-2 ${roleColors[user.role] || roleColors.sales} disabled:opacity-50`}
                    >
                      <option value="sales">Account Executive</option>
                      <option value="presales">Presales</option>
                      <option value="implementation">Implementation</option>
                      <option value="other">Other</option>
                      <option value="manager">Manager</option>
                      <option value="cfo">CFO</option>
                      <option value="cro">CRO</option>
                      <option value="cto">CTO</option>
                      <option value="ceo">CEO</option>
                      <option value="admin">Admin</option>
                      {userRole === 'super_admin' && (
                        <option value="super_admin">Super Admin</option>
                      )}
                    </select>
                    {user.id === currentUser.id && (
                      <div className="text-xs text-slate-500 mt-1">(You)</div>
                    )}
                  </td>
                  <td className="px-6 py-4 max-w-[180px]">
                    <div className="flex flex-wrap gap-1">
                      {rolePermissions[user.role]?.map((perm, idx) => (
                        <span key={idx} className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded whitespace-nowrap">
                          {perm}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="text-sm text-slate-600">
                      {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                    </div>
                    {user.login_count > 0 && (
                      <div className="text-xs text-slate-500">{user.login_count} logins</div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center">
                    {user.is_active ? (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                        <Check size={14} />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-semibold">
                        <X size={14} />
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center">
                    {canRemoveUsers ? (
                      <button
                        onClick={() => toggleUserStatus(user.id, user.is_active)}
                        disabled={updating[user.id] || user.id === currentUser.id}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                          user.is_active
                            ? 'bg-red-50 text-red-700 hover:bg-red-100'
                            : 'bg-green-50 text-green-700 hover:bg-green-100'
                        }`}
                      >
                        {user.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    ) : (
                      <span className="text-xs text-slate-400">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Role Legend */}
      <div className="mt-6 bg-slate-50 rounded-xl p-6">
        <h3 className="font-semibold text-slate-800 mb-4">Role Permissions</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9 gap-3">
          {Object.entries(rolePermissions).map(([role, perms]) => (
            <div key={role} className={`p-3 rounded-lg border-2 ${roleColors[role]}`}>
              <div className="font-semibold mb-2 text-sm">{roleLabels[role]}</div>
              <ul className="text-xs space-y-1">
                {perms.map((perm, idx) => (
                  <li key={idx} className="flex items-center gap-1">
                    <Check size={10} />
                    {perm}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UserAdminView;
